import { createTheme, PaletteOptions } from '@mui/material/styles';

const COLOR_PALETTE_CLASSIC = {
    primary: {
        main: '#0076EB',
        contrastText: '#FFFFFF', // White text on primary color
    },
    secondary: {
        main: '#00BF6F', // Bright secondary color
        contrastText: '#000000',
        light: '#EAF9F0',
    },
    error: {
        main: '#FF3131'
    },
    background: {
        default: '#FFFFFF',
        paper: '#f5f5f5',
    },
    text: {
        primary: '#000000',
        secondary: '#444444',
    },
    grey: {
        50: '#FFFFFF',
        100: '#f5f5f5',
        200: '#f2f2f2',
        300: '#EEEEEE',
        400: '#DDDDDD',
        600: '#666666',
        800: '#333333',
        900: '#111111',
    },
    // Add more palette colors like error, warning, info, and success if needed
} as const satisfies PaletteOptions

const classicTheme = createTheme({
    palette: COLOR_PALETTE_CLASSIC,
    typography: {
        fontFamily: [
            '-apple-system',
            'BlinkMacSystemFont',
            'Segoe UI',
            'Roboto',
            'Oxygen',
            'Ubuntu',
            'Cantarell',
            'Fira Sans',
            'Droid Sans',
            'Helvetica Neue',
            'sans-serif',
        ].join(','),
        h1: {
            fontSize: '2.5rem', // Large headings
            fontWeight: 600,
        },
        h2: {
            fontSize: '2rem', // Subheadings
            fontWeight: 500,
        },
        body1: {
            fontSize: '1rem', // Regular body text
        },
        body2: {
            fontSize: '0.875rem',
        },
    },
    spacing: 8, // Set spacing unit for consistent spacing
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    margin: 0,
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                },
                '@media print': {
                    '*': {
                        color: '#000000 !important',
                        backgroundColor: '#FFFFFF !important',
                        fontFamily: [
                            '-apple-system',
                            'BlinkMacSystemFont',
                            'Segoe UI',
                            'Roboto',
                            'Oxygen',
                            'Ubuntu',
                            'Cantarell',
                            'Fira Sans',
                            'Droid Sans',
                            'Helvetica Neue',
                            'sans-serif',
                        ].join(',') + ' !important',
                    },
                    'div': {
                        borderColor: '#000000 !important',
                    },
                    'h1, h2, h3, h4, h5, h6, p, span, div, td, th': {
                        color: '#000000 !important',
                    },
                },
                code: {
                    fontFamily: [
                        'source-code-pro',
                        'Menlo',
                        'Monaco',
                        'Consolas',
                        'Courier New',
                        'monospace',
                    ].join(','),
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    borderCollapse: 'separate',
                    borderSpacing: '0px',
                    width: '100%',
                    border: '1px solid #ccc',
                }
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    border: '1px solid #ccc',
                    padding: '8px',
                    '&:not(:first-of-type)': {
                        textAlign: 'left',
                    }
                },
                head: {
                    backgroundColor: COLOR_PALETTE_CLASSIC.grey[200],
                    fontWeight: 'bold',
                }
            }
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:nth-of-type(even)': {
                        backgroundColor: '#f9f9f9',
                    },
                    '&:hover': {
                        backgroundColor: COLOR_PALETTE_CLASSIC.grey[100],
                    }
                }
            }
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    '@media print': {
                        boxShadow: 'none',
                    }
                }
            }
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    '@media print': {
                        display: 'none',
                    }
                }
            }
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    '@media print': {
                        color: 'inherit',
                        textDecoration: 'none',
                    }
                }
            }
        },
        MuiContainer: {
            styleOverrides: {
                root: {
                    // marginTop: '80px',
                    '@media print': {
                        marginTop: 0,
                    }
                }
            }
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: COLOR_PALETTE_CLASSIC.grey[900],
                    '&.Mui-focused': {
                        color: COLOR_PALETTE_CLASSIC.grey[900],
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: 'inherit',
                    '&.Mui-focused': {
                        color: 'inherit',
                    },
                    '&.MuiInputLabel-shrink': {
                        color: 'inherit',
                    },
                },
                outlined: {
                    '&.Mui-focused': {
                        color: 'inherit',
                    },
                    '&.MuiInputLabel-shrink': {
                        color: 'inherit',
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    color: 'inherit',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                outlined: {
                    color: 'inherit',
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'inherit',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'inherit',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'inherit',
                    },
                },
                icon: {
                    color: 'inherit',
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'inherit',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'inherit',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'inherit',
                    },
                },
            },
        },
    }
});

const COLOR_PALETTE_BLOOMBERG = {
    primary: {
        main: '#00FF00', // Classic Bloomberg green
        contrastText: '#FFFFFF',
    },
    secondary: {
        main: '#FFFF00', // Bloomberg yellow
        contrastText: '#000000',
    },
    error: {
        main: '#FF0000'
    },
    background: {
        default: '#000000', // Black background
        paper: '#000000',
    },
    text: {
        primary: '#00FF00', // Green text
        secondary: '#FFFFFF', // White text
    },
    grey: {
        100: '#1A1A1A',
        200: '#333333',
        300: '#4D4D4D',
        400: '#666666',
        600: '#999999',
        800: '#CCCCCC',
    },
} as const satisfies PaletteOptions

const bloombergTheme = createTheme({
    palette: COLOR_PALETTE_BLOOMBERG,
    typography: {
        fontFamily: [
            'Inter',
            '-apple-system',
            'BlinkMacSystemFont',
            'Segoe UI',
            'Roboto',
            'Helvetica Neue',
            'Arial',
            'sans-serif'
        ].join(','),
        h1: {
            fontSize: '2rem',
            fontWeight: 600,
            color: '#00FF00',
            letterSpacing: '0.02em',
        },
        h2: {
            fontSize: '1.75rem',
            fontWeight: 500,
            color: '#00FF00',
            letterSpacing: '0.02em',
        },
        body1: {
            fontSize: '1rem',
            color: '#00FF00',
            letterSpacing: '0.02em',
        },
        body2: {
            fontSize: '0.875rem',
            color: '#00FF00',
            letterSpacing: '0.02em',
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: COLOR_PALETTE_BLOOMBERG.background.default,
                    color: COLOR_PALETTE_BLOOMBERG.text.primary,
                    margin: 0,
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                },
                '@media print': {
                    '*': {
                        color: '#000000 !important',
                        backgroundColor: '#FFFFFF !important',
                        fontFamily: [
                            '-apple-system',
                            'BlinkMacSystemFont',
                            'Segoe UI',
                            'Roboto',
                            'Oxygen',
                            'Ubuntu',
                            'Cantarell',
                            'Fira Sans',
                            'Droid Sans',
                            'Helvetica Neue',
                            'sans-serif',
                        ].join(',') + ' !important',
                        borderColor: '#000000 !important',
                    },
                    'h1, h2, h3, h4, h5, h6, p, span, div, td, th': {
                        color: '#000000 !important',
                    },
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundColor: COLOR_PALETTE_BLOOMBERG.background.default,
                    color: COLOR_PALETTE_BLOOMBERG.text.primary,
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    borderCollapse: 'separate',
                    borderSpacing: '0px',
                }
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    color: COLOR_PALETTE_BLOOMBERG.text.primary,
                    borderColor: COLOR_PALETTE_BLOOMBERG.grey[200],
                },
                head: {
                    backgroundColor: COLOR_PALETTE_BLOOMBERG.grey[100],
                    fontWeight: 'bold',
                },
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:nth-of-type(even)': {
                        backgroundColor: COLOR_PALETTE_BLOOMBERG.grey[100],
                    },
                    '&:hover': {
                        backgroundColor: COLOR_PALETTE_BLOOMBERG.grey[200],
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    backgroundColor: COLOR_PALETTE_BLOOMBERG.grey[100],
                    color: COLOR_PALETTE_BLOOMBERG.text.primary,
                    '&:hover': {
                        backgroundColor: COLOR_PALETTE_BLOOMBERG.grey[200],
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    backgroundColor: COLOR_PALETTE_BLOOMBERG.grey[100],
                    color: COLOR_PALETTE_BLOOMBERG.text.primary,
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    backgroundColor: COLOR_PALETTE_BLOOMBERG.grey[100],
                    color: COLOR_PALETTE_BLOOMBERG.text.primary,
                    '&:hover': {
                        backgroundColor: COLOR_PALETTE_BLOOMBERG.grey[200],
                    },
                    '&.Mui-focused': {
                        backgroundColor: COLOR_PALETTE_BLOOMBERG.grey[100],
                    },
                },
                icon: {
                    color: COLOR_PALETTE_BLOOMBERG.primary.main,
                },
                standard: {
                    '&:before': {
                        borderBottom: `1px solid ${COLOR_PALETTE_BLOOMBERG.grey[200]}`,
                    },
                    '&:hover:not(.Mui-disabled):before': {
                        borderBottom: `2px solid ${COLOR_PALETTE_BLOOMBERG.primary.main}`,
                    },
                    '&:after': {
                        borderBottom: `2px solid ${COLOR_PALETTE_BLOOMBERG.primary.main}`,
                    },
                },
                filled: {
                    backgroundColor: COLOR_PALETTE_BLOOMBERG.grey[200],
                    '&:hover': {
                        backgroundColor: COLOR_PALETTE_BLOOMBERG.grey[300],
                    },
                    '&.Mui-focused': {
                        backgroundColor: COLOR_PALETTE_BLOOMBERG.grey[200],
                    },
                },
                outlined: {
                    backgroundColor: 'transparent',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLOR_PALETTE_BLOOMBERG.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: COLOR_PALETTE_BLOOMBERG.primary.main,
                        borderWidth: '2px',
                    },
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: COLOR_PALETTE_BLOOMBERG.text.primary,
                    backgroundColor: COLOR_PALETTE_BLOOMBERG.grey[100],
                    '&.Mui-focused': {
                        color: COLOR_PALETTE_BLOOMBERG.text.primary,
                    },
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: COLOR_PALETTE_BLOOMBERG.text.primary,
                    '&.Mui-focused': {
                        color: COLOR_PALETTE_BLOOMBERG.text.primary,
                        backgroundColor: COLOR_PALETTE_BLOOMBERG.grey[100],
                    },
                    '&.MuiInputLabel-shrink': {
                        color: COLOR_PALETTE_BLOOMBERG.text.primary,
                        backgroundColor: COLOR_PALETTE_BLOOMBERG.grey[100],
                    },
                },
                outlined: {
                    '&.Mui-focused': {
                        color: COLOR_PALETTE_BLOOMBERG.text.primary,
                        backgroundColor: COLOR_PALETTE_BLOOMBERG.grey[100],
                    },
                    '&.MuiInputLabel-shrink': {
                        color: COLOR_PALETTE_BLOOMBERG.text.primary,
                        backgroundColor: COLOR_PALETTE_BLOOMBERG.grey[100],
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    color: '#000000',
                    '& .MuiChip-label': {
                        color: '#000000',
                    },
                },
            },
        },
    },
});

export { classicTheme, bloombergTheme };
